import { Directive, ElementRef, Output, EventEmitter, Input, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import debounce from 'lodash/debounce';

@Directive({
    selector: '[appEventLogObserver]',
    standalone: false
})
export class LogObserverDirective {
  private rootSelector = '.log-widget';

  @Input() endSentinelEl?: HTMLElement;
  @Output() isVisible = new EventEmitter<Element>();
  @Output() endReached = new EventEmitter<string>();
  @Output() virtualScroll = new EventEmitter<Element>();

  private destroy$ = new Subject<void>();
  private mainObserver?: IntersectionObserver;
  private endObserver?: IntersectionObserver;

  private latestVisibleTarget: Element | null = null;
  private emitVisibleDebounced = debounce(() => {
    if (!this.latestVisibleTarget) return;
    this.zone.run(() => this.isVisible.emit(this.latestVisibleTarget!));
    this.latestVisibleTarget = null;
  }, 300);

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      this.setupMainObserver();
      this.setupEndObserver();
    });
  }

  ngOnDestroy() {
    this.mainObserver?.disconnect();
    this.endObserver?.disconnect();
    this.emitVisibleDebounced.cancel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // 통합 Observer: 가시성(virtual scroll) + 스냅샷(snapshot loading)
  private setupMainObserver() {
    const root = this.findRoot(this.el.nativeElement, this.rootSelector);
    this.mainObserver = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            // virtualScroll: 가시성 토글 (즉시)
            this.zone.run(() => this.virtualScroll.emit(e.target));
            // isVisible: 스냅샷 로드 (debounced)
            this.latestVisibleTarget = e.target;
            this.emitVisibleDebounced();
          }
        }
      },
      {
        root,
        rootMargin: '200px 0px',
        threshold: 0,
      }
    );
    this.mainObserver.observe(this.el.nativeElement);
  }

  // End Observer: endSentinel 전용 (무한스크롤)
  private setupEndObserver() {
    const target = this.endSentinelEl;
    if (!target) return;

    const root = this.findRoot(this.el.nativeElement, this.rootSelector);
    this.endObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        const pagingKey =
          (entry.target as HTMLElement).dataset.pagingKey ||
          (entry.target as HTMLElement).id ||
          'end';
        this.zone.run(() => this.endReached.emit(pagingKey));
      },
      {
        root,
        rootMargin: '0px 0px 300px 0px',
        threshold: 0.01,
      }
    );

    this.endObserver.observe(target);
  }

  private findRoot(startEl: HTMLElement, selector?: string): Element | null {
    if (!selector) return null;
    const closest = startEl.closest(selector);
    if (closest) return closest;
    return startEl.ownerDocument.querySelector(selector);
  }
}
